/**
 * The `'dom'` sub-viewports the Control-raster pass (`ControlRasterPass.tsx`)
 * publishes, each with its dispatcher-absolute path and the resource scope of the
 * scene its Controls were authored in (ADR-0009, ADR-0013). Pure: the rendering
 * half is gated by `pnpm test:visual`.
 */
import { LTR_LAYOUT_ENV, resolveLayoutRtl } from '../../../godot/scene/control.js';
import { TWO_D_UI_TYPES } from '../../../r3f/controls/has2DUIContent.js';
import { liveTreeOf, } from '../../../r3f/liveSceneTree.js';
import { parentPathOf } from '../../../utils/nodePath.js';
import { isViewportBoundary } from './viewportBoundary.js';
import { resolveViewportSubtree, viewportContentKind } from './viewportContent.js';
/**
 * The layout direction `node`'s children inherit. `TWO_D_UI_TYPES` is this codebase's set of
 * Control-like types (`buildSolveTree.ts` reads it too). `CanvasLayer` is in it and states no
 * direction, which costs nothing: an unset `layout_direction` is inherited and relays what it was
 * given (`control.cpp:3555`).
 */
function rtlBelow(node, inheritedRtl, env) {
    return TWO_D_UI_TYPES.has(node.type)
        ? resolveLayoutRtl(node.properties.layoutDirection, inheritedRtl, env)
        : inheritedRtl;
}
/**
 * `Control::is_layout_rtl()` of the nearest ancestor Control above the node at `path`, or `null`
 * where there is none. The climb steps over a `SubViewport` (`control.cpp:3584-3598`), so a mixed
 * viewport's Controls start from the Control around it.
 */
export function inheritedRtlAt(tree, path, env) {
    const above = parentPathOf(path);
    if (above === null)
        return null;
    const link = tree.at(above);
    return link ? rtlBelow(link.step.node, inheritedRtlAt(tree, above, env), env) : null;
}
/**
 * Every Control-only sub-viewport in `roots`, depth-first, with its path and the
 * scope its Controls resolve in. A found sub-viewport is still descended into: a
 * nested one draws into its own target, and `ControlDispatcher` stops at it.
 */
export function collectControlRasterViewports(roots, sources, scope, layoutDirectionEnv = LTR_LAYOUT_ENV) {
    const found = [];
    const ctx = {
        externalResources: scope.externalResources,
        internalResources: scope.internalResources,
        ...sources,
    };
    // The step decides instance-collapse and per-group resource scope, the same decision
    // `useBuildSolveTree`'s walk makes for the on-screen pass.
    const tree = liveTreeOf(roots, ctx);
    tree.fold(null, (link, inheritedRtl, descend) => {
        const { step, path } = link;
        // Classified on the resolved subtree, as `useViewportContent` does: a
        // raw `instance=` child reads as 3D, so both owners of this key would
        // decline and leave the consumer blank.
        if (isViewportBoundary(step.node.type) &&
            viewportContentKind(resolveViewportSubtree(tree, link)) === 'dom') {
            found.push({ path, node: step.node, inheritedRtl, ...step.scope });
        }
        descend(rtlBelow(step.node, inheritedRtl, layoutDirectionEnv));
    });
    return found;
}
//# sourceMappingURL=controlRasterViewports.js.map